import { cn } from "@moe/oss/utils/utils";
import { useCombinedRefs } from "@moe/priv/hooks/use-combined-ref";
import { useTagSelect } from "@web/components/tag/use-tag-select";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useTagBox } from "./TagBox";

interface TagListProps {
  className?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  float?: boolean;
}

export const TagList = forwardRef<HTMLDivElement, TagListProps>(({ className, containerRef, float }, ref) => {
  const { suggestions, idx } = useTagBox();
  const localRef = useRef<HTMLDivElement>(null);
  const combinedRef = useCombinedRefs(localRef, ref);
  const suggestionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { open, setIDX } = useTagBox();
  const select = useTagSelect();
  const [topOffset, setTopOffset] = useState(0);

  /**
   * Handles resize to float under container
   */
  useEffect(() => {
    if (float && containerRef?.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          setTopOffset(height);
        }
      });

      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [float, containerRef]);

  /**
   * Scrolls the selected suggestion into view if it's not already visible
   */
  useEffect(() => {
    if (suggestions.length > 0 && localRef.current && suggestionRefs.current[idx]) {
      const container = localRef.current;
      const element = suggestionRefs.current[idx];

      if (element) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
          element.scrollIntoView({ behavior: "instant", block: "nearest" });
        }
      }
    }
  }, [idx, suggestions.length]);

  if (suggestions.length === 0 || !open) return null;

  return (
    <div
      ref={combinedRef}
      style={float ? { position: "absolute", top: `${topOffset}px`, width: "100%" } : undefined}
      className={cn(
        "border-line bg-float scroll-primary z-50 flex max-h-[280px] flex-col overflow-y-auto overscroll-contain scroll-auto rounded-md border px-2 shadow-lg",
        className
      )}
    >
      {suggestions.map((suggestion, index) => (
        <button
          key={suggestion}
          ref={(el) => (suggestionRefs.current[index] = el)}
          onPointerUp={() => {
            setIDX(index);
            select(index);
          }}
          onClick={(e) => {
            setIDX(index);
            select(index);
          }}
          onMouseEnter={() => {
            setIDX(index);
          }}
          className={cn(
            `text-tx-primary cursor-pointer rounded-sm px-2 py-2 text-start text-sm font-medium transition-opacity duration-200 ease-in-out ${index === idx && "bg-action-primary"}`
          )}
        >
          {suggestion}
        </button>
      ))}
    </div>
  );
});
